import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import { filter, take } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { stripHtml } from '../utils/seo-html.util';

const HOME_PATHS = new Set(['', '/']);
const LANDING_SLUGS: Record<string, string> = {
  '/gap-fakturowy': 'gapFakturowy',
  '/gap-casco': 'gapCasco',
  '/gap-flex': 'gapFlex',
  '/gap-leasing': 'gapLeasing',
  '/gap-dla-firm': 'gapDlaFirm',
  '/dla-jakich-pojazdow': 'dlaJakichPojazdow',
};
const NOINDEX_PREFIXES = [
  '/apk',
  '/cta',
  '/new-policy',
  '/new-warranty',
  '/payment-redirect',
];

const JSON_LD_ID = 'gap-seo-jsonld';

@Injectable({ providedIn: 'root' })
export class SeoService {
  private started = false;
  private jsonLdInjected = false;
  private routerBound = false;

  constructor(
    private readonly title: Title,
    private readonly meta: Meta,
    private readonly router: Router,
    private readonly transloco: TranslocoService,
    @Inject(DOCUMENT) private readonly document: Document,
  ) {}

  start(): void {
    if (this.started) {
      return;
    }
    this.started = true;

    const lang = this.transloco.getActiveLang() || 'pl';
    // Prerender: TranslocoFsLoader is async (Observable). Wait before Schema/meta.
    this.transloco
      .load(lang)
      .pipe(take(1))
      .subscribe(() => {
        this.applyForPath(this.router.url.split('?')[0].split('#')[0]);
        this.bindRouter();
      });
  }

  /** Exposed for unit tests. */
  buildHomepageJsonLd(): Record<string, unknown> {
    const base = (environment.baseUrl || 'https://gaponline.pl').replace(
      /\/$/,
      '',
    );
    const faqMainEntity = [1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => ({
      '@type': 'Question',
      name: stripHtml(this.transloco.translate(`faq.tile.${n}.question`)),
      acceptedAnswer: {
        '@type': 'Answer',
        text: stripHtml(this.transloco.translate(`faq.tile.${n}.answer`)),
      },
    }));

    const offers = [1, 2, 3].map((n) => ({
      '@type': 'Offer',
      name: stripHtml(this.transloco.translate(`home.tab.header${n}.short`)),
      description: stripHtml(
        this.transloco.translate(`home.tab.header${n}.cardDescription`),
      ),
      url: `${base}/`,
      availability: 'https://schema.org/InStock',
      priceCurrency: 'PLN',
    }));

    return {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Organization',
          '@id': `${base}/#organization`,
          name: 'GAP Online',
          url: `${base}/`,
          logo: `${base}/assets/images/gap_logo.svg`,
        },
        {
          '@type': 'WebSite',
          '@id': `${base}/#website`,
          url: `${base}/`,
          name: 'GAP Online',
          inLanguage: 'pl-PL',
          publisher: { '@id': `${base}/#organization` },
        },
        {
          '@type': 'Product',
          '@id': `${base}/#product-gap`,
          name: 'Ubezpieczenie GAP',
          description: stripHtml(
            this.transloco.translate('seo.home.description'),
          ),
          brand: { '@type': 'Brand', name: 'GAP Online' },
          url: `${base}/`,
          offers,
        },
        {
          '@type': 'FAQPage',
          '@id': `${base}/#faq`,
          mainEntity: faqMainEntity,
        },
      ],
    };
  }

  private bindRouter(): void {
    if (this.routerBound) {
      return;
    }
    this.routerBound = true;
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => {
        const path = event.urlAfterRedirects.split('?')[0].split('#')[0];
        this.applyForPath(path);
      });
  }

  private applyForPath(path: string): void {
    const normalized = path || '/';
    const landingSlug = LANDING_SLUGS[normalized];
    if (landingSlug) {
      this.applyLanding(landingSlug, normalized);
      return;
    }
    if (HOME_PATHS.has(normalized)) {
      this.applyHomepage();
      return;
    }
    if (
      NOINDEX_PREFIXES.some(
        (prefix) =>
          normalized === prefix || normalized.startsWith(`${prefix}/`),
      )
    ) {
      this.applyNoindex();
      return;
    }
    this.applyHomepage();
  }

  private filled(key: string): string {
    const value = this.transloco.translate(key);
    if (!value || value === key) {
      return '';
    }
    return value.trim();
  }

  private applyLanding(slug: string, path: string): void {
    const base = (environment.baseUrl || 'https://gaponline.pl').replace(
      /\/$/,
      '',
    );
    const canonical = `${base}${path}`;
    const title = this.filled(`seo.landing.${slug}.title`);
    const description = this.filled(`seo.landing.${slug}.description`);
    const h1 = this.filled(`seo.landing.${slug}.h1`);
    const indexable = Boolean(title && description && h1);
    const pageTitle = indexable
      ? title
      : this.transloco.translate('seo.funnel.title');

    this.title.setTitle(pageTitle);
    if (description) {
      this.meta.updateTag({ name: 'description', content: description });
      this.meta.updateTag({ property: 'og:description', content: description });
    }
    this.meta.updateTag({
      name: 'robots',
      content: indexable ? 'index,follow' : 'noindex,nofollow',
    });
    this.meta.updateTag({ property: 'og:title', content: pageTitle });
    this.meta.updateTag({ property: 'og:type', content: 'website' });
    this.meta.updateTag({ property: 'og:url', content: canonical });
    this.setCanonical(canonical);
    this.injectJsonLd(this.buildLandingJsonLd(canonical, h1 || pageTitle, base));
  }

  private buildLandingJsonLd(
    canonical: string,
    name: string,
    base: string,
  ): Record<string, unknown> {
    return {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'WebPage',
          name,
          url: canonical,
          isPartOf: { '@id': `${base}/#website` },
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'GAP Online',
              item: `${base}/`,
            },
            {
              '@type': 'ListItem',
              position: 2,
              name,
              item: canonical,
            },
          ],
        },
      ],
    };
  }

  private applyHomepage(): void {
    const pageTitle = this.transloco.translate('seo.home.title');
    const description = this.transloco.translate('seo.home.description');
    const base = (environment.baseUrl || 'https://gaponline.pl').replace(
      /\/$/,
      '',
    );
    const canonical = `${base}/`;
    const ogImage = `${base}/assets/images/og-image.png`;

    this.title.setTitle(pageTitle);
    this.meta.updateTag({ name: 'description', content: description });
    this.meta.updateTag({ name: 'robots', content: 'index,follow' });
    this.meta.updateTag({ property: 'og:title', content: pageTitle });
    this.meta.updateTag({ property: 'og:description', content: description });
    this.meta.updateTag({ property: 'og:type', content: 'website' });
    this.meta.updateTag({ property: 'og:url', content: canonical });
    this.meta.updateTag({ property: 'og:locale', content: 'pl_PL' });
    this.meta.updateTag({ property: 'og:image', content: ogImage });
    this.meta.updateTag({
      name: 'twitter:card',
      content: 'summary_large_image',
    });
    this.meta.updateTag({ name: 'twitter:title', content: pageTitle });
    this.meta.updateTag({ name: 'twitter:description', content: description });
    this.meta.updateTag({ name: 'twitter:image', content: ogImage });
    this.setCanonical(canonical);
    this.injectJsonLd(this.buildHomepageJsonLd());
  }

  private applyNoindex(): void {
    const pageTitle = this.transloco.translate('seo.funnel.title');
    this.title.setTitle(pageTitle);
    this.meta.updateTag({ name: 'robots', content: 'noindex,nofollow' });
  }

  private setCanonical(url: string): void {
    let link = this.document.querySelector(
      'link[rel="canonical"]',
    ) as HTMLLinkElement | null;
    if (!link) {
      link = this.document.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.document.head.appendChild(link);
    }
    link.setAttribute('href', url);
  }

  private injectJsonLd(payloadObject: Record<string, unknown>): void {
    const payload = JSON.stringify(payloadObject);
    let script = this.document.getElementById(JSON_LD_ID) as HTMLScriptElement | null;
    if (!script) {
      script = this.document.createElement('script');
      script.type = 'application/ld+json';
      script.id = JSON_LD_ID;
      this.document.head.appendChild(script);
    }
    script.text = payload;
    this.jsonLdInjected = true;
  }
}
